import { redirect } from '@tanstack/react-router';
import { useAuthStore } from '@/lib/stores/auth';

/**
 * Where to send authenticated users when they hit a public-only route (login,
 * register, OAuth/magic-link callbacks etc.). Mirrors `routes/index.tsx`'s
 * redirect target so authenticated users always land on the same place.
 */
export const AUTHENTICATED_LANDING = '/email-accounts' as const;

/**
 * Where to send unauthenticated users when they hit a protected route. The
 * `next` query param round-trips the original path so the login page can
 * resume the navigation after a successful sign-in.
 */
const LOGIN_PATH = '/login' as const;

/**
 * Reads the access token directly from the Zustand store. We deliberately
 * skip React-only hooks (`useAuthStore(selector)`) because `beforeLoad` runs
 * outside the render tree. `getState()` is the documented escape hatch and
 * returns the same hydrated state the rest of the app sees.
 */
function isAuthenticated(): boolean {
  return Boolean(useAuthStore.getState().accessToken);
}

type BeforeLoadCtx = {
  location: { pathname: string; href: string; search: Record<string, unknown> };
};

/**
 * `beforeLoad` guard for protected routes. Redirects to `/login?next=<path>`
 * when the user has no access token. Use on every route that should require
 * an authenticated session.
 *
 * Usage in a route file:
 *
 * ```ts
 * export const dashboardRoute = createRoute({
 *   getParentRoute: () => rootRoute,
 *   path: '/dashboard',
 *   beforeLoad: requireAuth,
 *   component: DashboardPage,
 * });
 * ```
 */
export function requireAuth({ location }: BeforeLoadCtx): void {
  if (isAuthenticated()) return;
  throw redirect({
    to: LOGIN_PATH,
    search: { next: location.href },
    replace: true,
  });
}

/**
 * `beforeLoad` guard for the inverse case — routes that should only be
 * reachable when the user is *not* authenticated (login, register, password
 * reset, etc.). Sends already-authenticated users to the app landing page.
 *
 * OAuth and magic-link callback routes intentionally do NOT use this guard:
 * their entire purpose is to exchange a token and *create* the session, so
 * they must remain reachable regardless of auth state.
 */
export function requireGuest(): void {
  if (!isAuthenticated()) return;
  throw redirect({ to: AUTHENTICATED_LANDING, replace: true });
}

/**
 * Resource-scoped routes ($id-parameterized), paired with the
 * workspace-neutral parent to redirect to after a workspace switch — the id
 * only resolves in the workspace it was opened from. Add an entry here for
 * every future `$id` route family; this is the one place that enforces it.
 *
 * The regex requires a numeric id segment so sibling static routes under the
 * same prefix (`/email-accounts/connect`, `/email-accounts/bulk-warmup-settings`)
 * don't match — those aren't resource-scoped and must survive a workspace
 * switch untouched (e.g. mid-OAuth-connect).
 */
const EMAIL_ACCOUNTS_LIST = '/email-accounts' as const;

const RESOURCE_SCOPED_ROUTES = [
  // /email-accounts/$id, /email-accounts/$id/activity, /email-accounts/$id/warmup-settings
  { prefix: /^\/email-accounts\/\d+(\/|$)/, redirectTo: EMAIL_ACCOUNTS_LIST },
] as const;

/**
 * Returns the workspace-neutral parent route to redirect to when `pathname`
 * is resource-scoped (see `RESOURCE_SCOPED_ROUTES` above), or `undefined`
 * when it isn't. Pure and router-agnostic on purpose — callers read the
 * pathname themselves (e.g. `useRouter().state.location.pathname`) and own
 * the actual navigation. Return type stays a literal union (via `as const`
 * above) rather than widening to `string`, so it type-checks directly against
 * `router.navigate({ to })`'s registered-route-path union at the call site.
 */
export function resourceScopedRedirectTarget(
  pathname: string,
): (typeof RESOURCE_SCOPED_ROUTES)[number]['redirectTo'] | undefined {
  return RESOURCE_SCOPED_ROUTES.find((r) => r.prefix.test(pathname))?.redirectTo;
}
